<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'

import { Button as KButton } from '@progress/kendo-vue-buttons'
import { SubmitInvoice } from '../services/CartService.ts'
import type { IAoeCart, IAoeInvoiceSubmitResponse, IAoeInvoiceResponse } from '../types/CartTypes.ts'

import AoeCartItemGrid from '../components/Controls/AoeCartItemGrid.vue'
import AoeOrderTotalsSummary from '../components/Controls/AoeOrderTotalsSummary.vue'
import AoePaymentControl from '../components/Controls/AoePaymentControl.vue'
import AoeInvoiceOrderSummary from '../components/Controls/AoeInvoiceOrderSummary.vue'

import Swal from "sweetalert2";
import { SetIsLoading } from '../services/AoeHelpers'
import { handleError } from '../services/ErrorService'
import { GetTermsUrl, GetPrivacyPolicyUrl, GetReplicatedSiteBusinessName } from '../stores/SettingsStore.ts'
import { UrlHelpers } from '../utils/urlHelpers.ts'
import { ref, onMounted } from 'vue'

const logger = useLogger()

const props = defineProps<{
  invoiceCart: IAoeInvoiceResponse | null
  cart: IAoeCart | null
  productImageUrls: Record<string, string>
  invoicenumber: string
  rootUrl: string
  emailAddress: string
}>()

  logger.debug('AoeInvoicePayment','props', props)

const emit = defineEmits<{
  'update-cart': []
}>()

const invoiceSubmitResp = ref<IAoeInvoiceSubmitResponse | null>(null)
const termsUrl = ref<string>('')
const privacyPolicyUrl = ref<string>('')

onMounted(async () => {
  const businessName = await GetReplicatedSiteBusinessName()
  // TermsUrl / PrivacyPolicyUrl are absolute settings URLs that skip the server-side
  // replicated-site rewrite, so re-apply it here to keep the user on /pws/{business}/.
  termsUrl.value = UrlHelpers.toReplicatedSiteUrl(await GetTermsUrl(), businessName)
  privacyPolicyUrl.value = UrlHelpers.toReplicatedSiteUrl(await GetPrivacyPolicyUrl(), businessName)
})

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler', event)
  emit('update-cart')
}

// Translates each submit error code with the same alerts.CartSubmit keys the other checkout flows use.
// Falls back to the server's generic message when there are no codes or a code has no translation.
function GetInvoiceSubmitErrorHtml(resp: IAoeInvoiceSubmitResponse): string {
  const messages: string[] = []
  let hasUntranslatedError = false

  for (const submitError of resp.submitErrors ?? []) {
    const messageKey = `alerts.CartSubmit.${submitError.errorCode}`
    const message = submitError.attributes ? t(messageKey, submitError.attributes) : t(messageKey)
    if (message === messageKey) {
      hasUntranslatedError = true
    } else if (!messages.includes(message)) {
      messages.push(message)
    }
  }

  if ((messages.length === 0 || hasUntranslatedError) && resp.invoiceSubmitErrors?.length) {
    messages.push(resp.invoiceSubmitErrors[0])
  }

  return messages.map(EscapeHtml).join('<br>')
}

function EscapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

async function submitInvoice(event: MouseEvent) {
  logger.debug('submitInvoice', event)

  if (!props.invoicenumber) return

  try {
    SetIsLoading(true)
    const data = await SubmitInvoice({ invoiceNumber: props.invoicenumber, emailAddress: props.emailAddress })

    if (data != null)
    {
      invoiceSubmitResp.value = data
      if (invoiceSubmitResp.value != null && invoiceSubmitResp.value.invoiceSubmitErrors != null)
      {
        Swal.fire({
          title: t("alerts.SubmitInvoiceError"),
          html: t("alerts.SubmitInvoiceErrorSubTitle", [GetInvoiceSubmitErrorHtml(invoiceSubmitResp.value)]),
          icon: "warning",
          confirmButtonText: t("buttons.Okay"),
        })
      }
      else
      {
        if (invoiceSubmitResp.value != null && invoiceSubmitResp.value.orderSubmitted)
        {
          Swal.fire({
            title: t("alerts.SubmitInvoiceSuccess"),
            text: t("alerts.SubmitInvoiceSuccessWithOrder"),
            icon: "success",
            confirmButtonText: t("buttons.Okay"),
          })
          .then((result) => {
            if (result.isConfirmed) {
              logger.debug('AoeInvoicePayment','submitInvoice redirecting to rootUrl', props.rootUrl)
              window.location.href = props.rootUrl
            }
          })
        }
        else
        {
          Swal.fire({
            title: t("alerts.SubmitInvoiceSuccess"),
            text: t("alerts.SubmitInvoiceSuccessWithOutOrder"),
            icon: "success",
            confirmButtonText: t("buttons.Okay"),
          })
          .then((result) => {
            if (result.isConfirmed) {
              logger.debug('AoeInvoicePayment','submitInvoice redirecting to rootUrl', props.rootUrl)
              window.location.href = props.rootUrl
            }
          })
        }
      }
    }
  } catch (error) {
    logger.error('SubmitInvoice error', error)
    await handleError(error, t)
  } finally {
    SetIsLoading(false)
  }
}

</script>

<style lang="scss">
/* Updated styling to match application design patterns */
.aoe-invoice-payment {
  color: #333;
}

/* Global header styling for all components */
.section-header-div,
.party-order-entry-section-header-div {
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-4;
  background-color: transparent;
  border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark));
  margin-bottom: 0;
}

.invoice-section-header,
.shopping-cart-section-header,
.order-summary-section-header,
.payment-control-section-header {
  color: var(--primary-text-color, var(--vt-c-teal-dark));
  letter-spacing: 0.5px;
}

.invoice-instruction-div {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-radius: 0 0 6px 6px;
  margin-bottom: $spacing-4;
  box-shadow: 0 2px 4px black;
}

.invoice-instr-label {
  @include typography-small;
  line-height: 1.5;
  color: #555;
}

.invoice-order-summary-div,
.invoice-cart-item-grid-div,
.aoe-order-summary-div,
.aoe-payment-control-div {
  margin-bottom: 20px;
  border-radius: $radius-md;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  overflow: hidden;
}

.aoe-invoice-submit-div {
  margin: $spacing-8 0;
}

.invoice-submit-button {
  @include font-weight-medium;
  min-width: 150px;
  border-radius: $button-radius-lg;
}

.aoe-invoice-payment {
  .terms-agreement {
    @include typography-body;
    color: #666;
  }

  .terms-agreement a {
    color: #0066cc;
    text-decoration: none;
  }

  .terms-agreement a:hover {
    text-decoration: underline;
  }

  .terms-agreement.checkout-terms-agreement {
    font-size: .9rem !important;
  }

  .checkout-terms-agreement a {
    text-decoration: underline;
  }
}
</style>

<template>
  <div class="aoe-invoice-payment">
    <div class="row section-header-row">
      <div class="col-12 section-header-div invoice-section-header">
        {{ t('invoice.InvoiceHeader') }}
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 invoice-instruction-div">
        <span class="invoice-instr-label" v-html="t('invoice.InvoiceInstruction')"></span>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 invoice-order-summary-div">
        <AoeInvoiceOrderSummary
          :invoicecart="invoiceCart"
        ></AoeInvoiceOrderSummary>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 invoice-cart-item-grid-div">
        <AoeCartItemGrid
          :cart="cart"
          :productimageurls="productImageUrls"
          :editable="false"
        ></AoeCartItemGrid>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 aoe-order-summary-div">
        <AoeOrderTotalsSummary
          :cart="cart"
        ></AoeOrderTotalsSummary>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 aoe-payment-control-div">
        <AoePaymentControl
          :carts="cart != null ? [cart] : [{} as IAoeCart]"
          :showdeletebutton="true"
          :showinvoice="false"
          :allowaddpayment="true"
          :excludedControlFolderNames="['OPA']"
          @update-cart="updateCartHandler($event)"
        ></AoePaymentControl>
      </div>
    </div>
    <div v-if="cart?.balance == 0" class="row mt-2">
      <div class="col-12 terms-agreement checkout-terms-agreement">
        <i18n-t keypath="terms.checkoutAgreement" tag="p">
          <template #termsLink>
            <a :href="termsUrl" target="_blank">{{ t('terms.checkoutTermsOfUse') }}</a>
          </template>
          <template #privacyLink>
            <a :href="privacyPolicyUrl" target="_blank">{{ t('terms.checkoutPrivacyPolicy') }}</a>
          </template>
        </i18n-t>
      </div>
    </div>
    <div v-if="cart?.balance == 0"  class="row mt-3">
      <div  class="col-12 aoe-invoice-submit-div text-center">
          <KButton
            :rounded="'large'"
            class="baseBtn primaryBtn invoice-submit-button"
            @click="submitInvoice($event)">
            {{ t('invoice.NewSubmitButton') }}
          </KButton>
      </div>
    </div>
  </div>
</template>
